// @flow
import type { FetchedCurrency } from "@kiwicom/nitro/lib/records/Currency"
import Moment from "moment"

import type { PassengerId } from "mmb/types/primitiveTypes"
import type { RefundMethod } from "mmb/records/bookingInput"

export const VIEWS = {
  REASON: "REASON",
  CALCULATOR: "CALCULATOR",
  GUIDE: "GUIDE",
  GENERAL: "GENERAL",
  MEDICAL: "MEDICAL",
  CONTACT: "CONTACT",
  SUMMARY: "SUMMARY",
  THANKYOU: "THANKYOU",
}

export const REASONS = {
  CANCELLATION_GUIDE: "cancellation_guide",
  BOOKING_CANCELLATION: "booking_cancelation",
  GUARANTEE: "cancel",
  MEDICAL: "medical",
  BAGGAGE: "baggage",
  BOOKING: "booking",
  CHECKIN: "checkin",
  ASSISTED_REFUND: "assisted_refund",
}

export const FORM_NAMES = {
  GUIDE: "bookingRefundGuide",
  GENERAL: "bookingRefundGeneral",
  MEDICAL: "bookingRefundMedical",
  MEDICAL_CONTAINER: "bookingRefundMedicalContainer",
  CONTACT: "bookingRefundContact",
}

export const PASSENGER_MEDICAL_REASONS = {
  MEDICAL: "medical",
  DEATH: "death",
  DEATH_RELATIVE: "death_relative",
}
export type IndexedReason = "cancellation_guide" | "cancel" | "baggage" | "booking" | "checkin"
export type IndexedReasons = Array<IndexedReason>

export const REASONS_TO_SELECTION: IndexedReasons = [
  REASONS.CANCELLATION_GUIDE,
  REASONS.GUARANTEE,
  REASONS.BAGGAGE,
  REASONS.CHECKIN,
  REASONS.BOOKING,
]

export const STEP_DIRECTION = {
  NEXT: "next",
  PREV: "previous",
}

export const STEPS_PATH = {
  // GUIDE
  [REASONS.MEDICAL]: {
    previous: VIEWS.GUIDE,
    next: VIEWS.MEDICAL,
    [VIEWS.MEDICAL]: {
      next: VIEWS.CONTACT,
      [VIEWS.CONTACT]: {
        next: VIEWS.SUMMARY,
        [VIEWS.SUMMARY]: {
          next: VIEWS.THANKYOU,
        },
      },
    },
  },
  [REASONS.BOOKING_CANCELLATION]: {
    previous: VIEWS.GUIDE,
    next: VIEWS.GENERAL,
    [VIEWS.GENERAL]: {
      next: VIEWS.CONTACT,
      [VIEWS.CONTACT]: {
        next: VIEWS.THANKYOU,
      },
    },
  },
  // REASON SELECTION
  [REASONS.GUARANTEE]: {
    previous: VIEWS.REASON,
    next: VIEWS.GENERAL,
    [VIEWS.GENERAL]: {
      next: VIEWS.CONTACT,
      [VIEWS.CONTACT]: {
        next: VIEWS.THANKYOU,
      },
    },
  },
  [REASONS.BAGGAGE]: {
    previous: VIEWS.REASON,
    next: VIEWS.GENERAL,
    [VIEWS.GENERAL]: {
      next: VIEWS.CONTACT,
      [VIEWS.CONTACT]: {
        next: VIEWS.THANKYOU,
      },
    },
  },
  [REASONS.BOOKING]: {
    previous: VIEWS.REASON,
    next: VIEWS.GENERAL,
    [VIEWS.GENERAL]: {
      next: VIEWS.CONTACT,
      [VIEWS.CONTACT]: {
        next: VIEWS.THANKYOU,
      },
    },
  },
  [REASONS.CHECKIN]: {
    previous: VIEWS.REASON,
    next: VIEWS.GENERAL,
    [VIEWS.GENERAL]: {
      next: VIEWS.CONTACT,
      [VIEWS.CONTACT]: {
        next: VIEWS.THANKYOU,
      },
    },
  },
}

type ApplicationsToReasons = {
  [$Values<typeof REASONS>]: $Values<typeof REASONS>,
}

export const APPLICATIONS_TO_REASONS: ApplicationsToReasons = {
  [REASONS.BOOKING_CANCELLATION]: REASONS.CANCELLATION_GUIDE,
  [REASONS.MEDICAL]: REASONS.CANCELLATION_GUIDE,
  [REASONS.ASSISTED_REFUND]: REASONS.CANCELLATION_GUIDE,
  [REASONS.GUARANTEE]: REASONS.GUARANTEE,
  [REASONS.BAGGAGE]: REASONS.BAGGAGE,
  [REASONS.BOOKING]: REASONS.BOOKING,
  [REASONS.CHECKIN]: REASONS.CHECKIN,
}

export const REASON_TITLES = {
  [REASONS.CANCELLATION_GUIDE]: __("refund.reasons.guide.title"),
  [REASONS.BOOKING_CANCELLATION]: __("refund.reasons.booking_cancelation.title"),
  [REASONS.GUARANTEE]: __("result.guarantee.title"), // REASONS.HOTELS deprecated - merged with CANCEL reason
  [REASONS.MEDICAL]: __("refund.reasons.medical.title"),
  [REASONS.BAGGAGE]: __("refund.reason_baggage"),
  [REASONS.BOOKING]: __("refund.reasons.booking.title"),
  [REASONS.CHECKIN]: __("refund.reason_checkin"),
}

export const REASON_LISTS = {
  [REASONS.CANCELLATION_GUIDE]: [
    __("refund.reasons.booking_cancelation.list.first"),
    __("refund.reasons.missed.list.first"),
    __("refund.reasons.medical.list.first"),
  ],
  [REASONS.BOOKING_CANCELLATION]: [__("refund.reasons.booking_cancelation.list.first")],
  [REASONS.GUARANTEE]: [
    __("refund.reasons.cancel.list.first"),
    __("refund.reasons.cancel.list.second"),
  ],
  [REASONS.MEDICAL]: [
    __("refund.reasons.medical.list.first"),
    __("refund.reasons.medical.list.second"),
  ],
  [REASONS.BAGGAGE]: [__("refund.reasons.baggage.list.first")],
  [REASONS.BOOKING]: [__("refund.reasons.booking.list.first")],
  [REASONS.CHECKIN]: [__("refund.reasons.checkin.list.first")],
  [REASONS.ASSISTED_REFUND]: [],
}

export const REASON_ICONS = {
  [REASONS.CANCELLATION_GUIDE]: "/images/illust/refundForm/airplane.svg",
  [REASONS.BOOKING_CANCELLATION]: "/images/illust/refundForm/airplane.svg",
  [REASONS.GUARANTEE]: "/images/illust/refundForm/insurance-confirmed.svg",
  [REASONS.MEDICAL]: "/images/illust/refundForm/wheelchair.svg",
  [REASONS.BAGGAGE]: "/images/illust/refundForm/baggages.svg",
  [REASONS.BOOKING]: "/images/illust/refundForm/wallet.svg",
  [REASONS.CHECKIN]: "/images/illust/refundForm/ticket-flight.svg",
}

// the following reasons do not require affected passengers or flights to be selected
export const IS_VALID_WITHOUT_AFFECTED = {}

// help center IDs
export const SUGGESTED_ARTICLES = {
  [REASONS.CANCELLATION_GUIDE]: [],
  [REASONS.GUARANTEE]: [28, 25, 118],
  [REASONS.BOOKING]: [],
  [REASONS.BOOKING_CANCELLATION]: [],
  [REASONS.BAGGAGE]: [45, 103],
  [REASONS.CHECKIN]: [108, 90, 58],
  [REASONS.MEDICAL]: [],
  [REASONS.ASSISTED_REFUND]: [],
}

export const ATTACHMENTS_REQUIRED = {
  [REASONS.MEDICAL]: true,
  [REASONS.CHECKIN]: true,
  [REASONS.BAGGAGE]: true,
}

export const REQUIRED_ATTACHMENTS_TITLE_KEYS = {
  [REASONS.CANCELLATION_GUIDE]: null,
  [REASONS.GUARANTEE]: __("refund.attachments_might_be_required"),
  [REASONS.BOOKING]: __("refund.attachments_might_be_required"),
  [REASONS.BAGGAGE]: __("refund.attachments_might_be_required"),
  [REASONS.CHECKIN]: __("refund.attachments_might_be_required"),
  [REASONS.MEDICAL]: __("refund.attachments_required"),
  [REASONS.BOOKING_CANCELLATION]: null, // section hidden
  [REASONS.ASSISTED_REFUND]: null, // section hidden
}

export const REQUIRED_ATTACHMENTS_BULLET_KEYS = {
  [REASONS.CANCELLATION_GUIDE]: null,
  [REASONS.GUARANTEE]: [__("refund.notes.guarantee"), __("refund.notes.check_copies")],
  [REASONS.BOOKING]: [__("refund.notes.airport_expenses"), __("refund.notes.check_copies")],
  [REASONS.BAGGAGE]: [__("refund.notes.airport_expenses"), __("refund.notes.check_copies")],
  [REASONS.CHECKIN]: [__("refund.notes.airport_expenses"), __("refund.notes.check_copies")],
  [REASONS.MEDICAL]: [__("refund.notes.medical")],
  [REASONS.BOOKING_CANCELLATION]: null, // section hidden
  [REASONS.ASSISTED_REFUND]: null, // section hidden
}

export const CURRENT_VIEW_CONTENT = {
  [VIEWS.REASON]: {
    title: __("mmb.header.refund"),
  },
  [VIEWS.GUIDE]: {
    title: __("refund.steps.refund"),
  },
  [VIEWS.CALCULATOR]: {
    title: __("refund.steps.calculator"),
  },
  [VIEWS.GENERAL]: {
    title: __("refund.steps.complete"),
  },
  [VIEWS.MEDICAL]: {
    title: __("refund.steps.complete"),
  },
  [VIEWS.CONTACT]: {
    title: __("refund.steps.refund_method"),
  },
  [VIEWS.SUMMARY]: {
    title: __("refund.steps.summary"),
  },
  [VIEWS.THANKYOU]: {
    title: "",
  },
}

// !ORDER MATTERS
export const METHODS = {
  CREDITS: "credits",
  ZOOZ: "zooz",
  PAYU: "payu",
  CUP: "cup",
  TRUSTLY: "trustly",
  SOFORT: "sofort",
  ALIPAY: "alipay",
  PAYPAL: "paypal",
  BANK_ACCOUNT: "bank_account",
}

export const MEDICAL_DOCUMENT_TYPES = {
  MEDICAL: "medical_files",
  DEATH: "death_files",
  RELATIVE: "relative_relationship_files",
}

export const MEDICAL_DOCUMENT_TYPES_KEYS = {
  [MEDICAL_DOCUMENT_TYPES.MEDICAL]: __("refund.medical.upload.medical_certificate"),
  [MEDICAL_DOCUMENT_TYPES.DEATH]: __("refund.medical.upload.death_certificate"),
  [MEDICAL_DOCUMENT_TYPES.RELATIVE]: __("refund.medical.upload.proof_relative"),
}

export const CALCULATOR_TYPES = {
  INSTANT: "instant",
  ASSISTED: "assisted",
}

export const FOOTER_CONTINUE_TYPES = {
  CONTINUE: "continue",
  REQUEST_REFUND: "requestRefund",
  CANCEL_SELECTED_FLIGHTS: "cancelSelectedFlight",
  CANCELLATION_FEE_PAYMENT: "cancellationFeePayment",
}

export type Method = $Values<typeof METHODS>

export type View = $Values<typeof VIEWS>

export type Reason = $Values<typeof REASONS>

export type StepDirection = $Values<typeof STEP_DIRECTION>

export type MedicalDocumentType = $Values<typeof MEDICAL_DOCUMENT_TYPES>

export type CalculatorType = $Values<typeof CALCULATOR_TYPES>

export type MedicalFormData = {
  reason: Reason,
  medical_files: Array<any>,
  death_files: Array<any>,
  relative_medical_files: Array<any>,
  relative_relationship_files: Array<any>,
}

export type Document = {
  name: string,
  pid: PassengerId,
  type: MedicalDocumentType,
}

export type FooterContinueType = $Values<typeof FOOTER_CONTINUE_TYPES>

export type AssistedRefundType = {
  price: {
    amount: number,
    currency: FetchedCurrency,
  },
}

export type RefundFormDataType = {
  reason: Reason,
  details: string,
  proceessType: boolean,
}

export type RefundApplicationPayload = {
  affected_passenger_ids: Array<PassengerId>,
  affected_segment_codes: Array<string>,
  details: string,
  email: string,
  files: any,
  refund_method: RefundMethod,
  refund_method_data: {
    // with "paypal" and "credit" method:
    payment_email?: string,
    // with "bank" method:
    bank_IBAN?: string,
    bank_SWIFT?: string,
    bank_holder?: string,
  },
  phone: string,
  process: "proceed" | "assessAmount",
  reason: string,
}

export type RefundApplication = {
  created: Moment,
  paymentMethod: string, // e.g. bookingCard
  reason: Reason,
}
